import { FieldViewModel, isFieldViewModel } from "../../../fields";
import { Layer } from "../../../map/layer";
import { getSelectedStrokeStyle } from "../../../map/styles/defaults";
import { getTextStyles } from "../../../map/styles/text";
import { str } from "../../../strings";
import { ol_style, ol_geom } from "@omnia/exports/openlayers";

type FieldsLayerOptions = {
    /**
     * Should the layer be visible by default?
     *
     * (If left `undefined`, default is `true`)
     *
     * @default true
     */
    visible?: boolean;

    /**
     * Should the layer labels be visible by default?
     *
     * (If left `undefined`, default is `true`)
     *
     * @default true
     */
    showLabels?: boolean;

    /**
     * If `true` the layer is ALWAYS visible, and the user may not change the visibility from the map-options popup. (Can still be changed via code)
     *
     * (If left `undefined`, default is `false`)
     *
     * @default false
     */
    alwaysVisible?: boolean;

    /**
     * Whether the layer should be visible & controllable from the "Map Options" popover panel.
     *
     * (If left `undefined`, default is `true`. Note this is different to the *Layer* default.)
     *
     * @default true
     */
    layersPopup?: boolean;

    /**
     * Whether features on this layer are selectable by default.
     *
     * (If left `undefined`, default is `true`)
     *
     * @default true
     */
    selectable?: boolean;
};

export class FieldsLayer extends Layer {
    public static readonly LOCAL_STORAGE_KEY = "fields" as const;

    /**
     * This variable is used to control whether the "fields" layer should hide its labels
     * because the "croppings" layer is visible & that layer's labels should take precedence.
     */
    private _showFieldNames: boolean = true;

    public fillColorProvider: (field: FieldViewModel) => number[] | string;

    constructor(options: FieldsLayerOptions) {
        super({
            settings: {
                visible: options.visible ?? true,
                showLabels: options.showLabels ?? true,
                alwaysVisible: options.alwaysVisible ?? false,
            },
            layersPopup: options.layersPopup ?? true,
            selectable: options.selectable ?? true,
        });
    }

    get name(): string {
        return str.Fields;
    }

    /**
     * This property is used to control whether the "fields" layer should hide its labels
     * because the "croppings" layer is visible & that layer's labels should take precedence.
     */
    get showFieldNames() {
        return this._showFieldNames;
    }

    /**
     * This property is used to control whether the "fields" layer should hide its labels
     * because the "croppings" layer is visible & that layer's labels should take precedence.
     */
    set showFieldNames(value: boolean) {
        if (value != this._showFieldNames) {
            this._showFieldNames = value;
            this.refresh();
        }
    }

    findFields(fieldIds: Iterable<number>) {
        const set = new Set(fieldIds);
        return this.findFeatures((_, d) => isFieldViewModel(d) && set.has(d.fieldId));
    }

    findField(fieldId: number) {
        const m = this.findFeatures((_, d) => isFieldViewModel(d) && d.fieldId === fieldId);
        return m ? m[0] : undefined;
    }

    setFields(fields: FieldViewModel[], geometryProvider?: (from: FieldViewModel) => ol_geom.Geometry) {
        setFieldsLayerFeatures(this, fields, geometryProvider);
    }

    highlight(fields: FieldViewModel[]) {
        return super.highlightFeaturesWithData(fields, (f) => f.getId());
    }

    getLayerStyleFunction(selected: boolean): (feature, resolution) => any {
        let stroke = new ol_style.Stroke({
            color: [0, 0, 0, 1],
            width: 3,
        });

        let selectedStroke = getSelectedStrokeStyle();

        let muted = new ol_style.Style({
            stroke: new ol_style.Stroke({
                color: [0, 0, 0, 1],
                width: 2,
                lineDash: [3, 3],
            }),
        });

        return (feature, resolution) => {
            let data = <FieldViewModel>feature.get("data");

            if (data.disabled) {
                return data.ignoreBoundary ? [] : [muted];
            }

            let highlight = this.isFeatureHighlighted(feature);

            if (highlight === false) {
                return [muted];
            }

            let fillColor: number[] | string;

            if (this.fillColorProvider) {
                fillColor = this.fillColorProvider(data);
            }

            if (!fillColor) {
                let opacity = this.fillSelectedStyle ? 1 : 0.1;
                fillColor = [255, 255, 255, opacity * (selected ? 0.5 : 0.15)];
            }

            let styles = [];

            const field = data?.field;
            if (highlight) selected = true;

            styles.push(
                new ol_style.Style({
                    fill: fillColor ? new ol_style.Fill({ color: fillColor }) : null,
                    stroke: selected ? selectedStroke.outer.getStroke() : stroke,
                })
            );

            if (selected) {
                styles.push(selectedStroke.inner);
            }

            if (this.showLabels && this._showFieldNames && this.getFeatureLabelVisibility(feature)) {
                const texts = [{ text: field.Name, fontSize: 14 }];

                const textStyles = getTextStyles(resolution, texts);

                styles = styles.concat(textStyles);
            }

            return styles;
        };
    }
}

export function setFieldsLayerFeatures(
    layer: Layer,
    items: FieldViewModel[],
    geometryProvider: (from: FieldViewModel) => ol_geom.Geometry
) {
    geometryProvider = geometryProvider ?? ((a) => undefined);

    const getBoundary = (f: FieldViewModel) => {
        return geometryProvider(f) ?? f.getBoundary();
    };

    const features = layer.createFeatures(items, getBoundary, (i) => i.getId());

    const hiddenLabels = new Set<string>(
        layer
            .getFeatures()
            .filter((l) => !!l.get("hideLabel"))
            .map((l) => l.get("data").getId())
    );

    for (const f of features) {
        const d = f.get("data") as FieldViewModel;
        f.set("hideLabel", hiddenLabels.has(d.getId()));
    }

    layer.setFeatures(features);

    return features;
}
